/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

/**
 * Re-runs all header dimension updates (same path as viewport resize).
 */
function scheduleHeaderDimensionRefresh() {
  const refresh = window.refreshHeaderLayout || window.setDimensionVariables;
  if (typeof refresh !== 'function') return;

  refresh();
  requestAnimationFrame(refresh);
  window.setTimeout(refresh, 150);
  window.setTimeout(refresh, 450);
  window.setTimeout(refresh, 900);
}

const debouncedHeaderDimensionRefresh = debounce(scheduleHeaderDimensionRefresh, 150);

/**
 * True when the GTranslate select has a real language (not the empty placeholder).
 * @param {HTMLSelectElement|null} select
 * @returns {boolean}
 */
function gtSelectorHasLanguage(select) {
  if (!select?.options?.length) return false;

  const selected = select.options[select.selectedIndex];
  if (!selected) return false;

  const value = (select.value || selected.value || '').trim();
  if (value) return true;

  const label = (selected.textContent || '').trim().toLowerCase();
  return label.length > 0 && label !== 'select';
}

let gtSelectorInitialRefreshDone = false;
let gtSelectorWatchObserver = null;
let gtSelectorWatchInterval = null;
let gtSelectorWatchTimeout = null;

function stopGtSelectorWatch() {
  if (gtSelectorWatchObserver) {
    gtSelectorWatchObserver.disconnect();
    gtSelectorWatchObserver = null;
  }
  if (gtSelectorWatchInterval) {
    clearInterval(gtSelectorWatchInterval);
    gtSelectorWatchInterval = null;
  }
  if (gtSelectorWatchTimeout) {
    clearTimeout(gtSelectorWatchTimeout);
    gtSelectorWatchTimeout = null;
  }
}

function onGtSelectorLanguageReady() {
  if (gtSelectorInitialRefreshDone) return;
  gtSelectorInitialRefreshDone = true;
  stopGtSelectorWatch();
  scheduleHeaderDimensionRefresh();
}

function tryGtSelectorLanguageReady() {
  const select = document.querySelector('.gt_selector');
  if (!gtSelectorHasLanguage(select)) return false;
  onGtSelectorLanguageReady();
  return true;
}

/**
 * On first load, wait for GTranslate to inject .gt_selector with a selected language.
 */
function waitForGtSelectorLanguage() {
  if (tryGtSelectorLanguageReady()) return;

  const watchRoots = [
    document.querySelector('.announcement__localization--app'),
    document.querySelector('.announcement__localization'),
    document.querySelector('.footer__localization'),
    document.body
  ].filter(Boolean);

  gtSelectorWatchObserver = new MutationObserver(() => {
    tryGtSelectorLanguageReady();
    if (gtSelectorHasLanguage(document.querySelector('.gt_selector'))) {
      debouncedHeaderDimensionRefresh();
    }
  });

  watchRoots.forEach((root) => {
    gtSelectorWatchObserver.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['value', 'selected']
    });
  });

  const headerNav = document.querySelector('.main-nav');
  if (headerNav) {
    gtSelectorWatchObserver.observe(headerNav, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  gtSelectorWatchInterval = window.setInterval(() => {
    if (tryGtSelectorLanguageReady()) return;
    if (gtSelectorHasLanguage(document.querySelector('.gt_selector'))) {
      debouncedHeaderDimensionRefresh();
    }
  }, 200);

  gtSelectorWatchTimeout = window.setTimeout(stopGtSelectorWatch, 20000);
}

document.addEventListener('change', (evt) => {
  if (evt.target?.classList?.contains('gt_selector') || evt.target?.classList?.contains('market-language-selector')) {
    scheduleHeaderDimensionRefresh();
  }
});

document.addEventListener('click', (evt) => {
  if (evt.target.closest('.market-language-select__trigger')) {
    scheduleHeaderDimensionRefresh();
  }
});

function startGtSelectorWatch() {
  if (typeof window.refreshHeaderLayout === 'function') {
    waitForGtSelectorLanguage();
  } else {
    window.setTimeout(startGtSelectorWatch, 50);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startGtSelectorWatch);
} else {
  startGtSelectorWatch();
}

if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.menu = document.querySelector('.main-menu__content');
      this.isPaused = false;

      this.bindEvents();
      this.init();
    }

    disconnectedCallback() {
      if (this.moveLinksHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLinksHandler);
      }
    }

    bindEvents() {
      if (this.links.length) {
        this.moveLinksHandler = this.moveLinksHandler || this.moveLinks.bind(this);
        window.addEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.slider) {
        this.slider.addEventListener('mouseenter', this.pauseSlider.bind(this));
        this.slider.addEventListener('mouseleave', this.resumeSlider.bind(this));
      }
    }

    /**
     * Start sliders a slidin'
     */
    init() {
      if (this.slider) {
        const slides = this.slider.querySelectorAll('.announcement__text');
        slides[0].classList.add('is-visible');

        const nextSlide = () => {
          if (!this.isPaused && theme.elementUtil.isInViewport(this)) {
            const currSlide = this.slider.querySelector('.announcement__text.is-visible');
            const currSlideIndex = Array.from(currSlide.parentNode.children)
              .indexOf(currSlide);
            const nextSlideIndex = currSlideIndex + 1 < slides.length ? currSlideIndex + 1 : 0;

            if (document.dir === 'rtl') {
              this.slider.scrollTo(
                -Math.abs(nextSlideIndex * slides[nextSlideIndex].clientWidth),
                0
              );
            } else {
              this.slider.scrollTo(slides[nextSlideIndex].offsetLeft - 32, 0);
            }

            slides[nextSlideIndex].classList.add('is-visible');
            slides[currSlideIndex].classList.remove('is-visible');
          }

          setTimeout(nextSlide, this.dataset.slideDelay);
        };
        setTimeout(nextSlide, this.dataset.slideDelay);
      }

      if (this.links.length) {
        this.moveLinks();
      }
    }

    /**
     * Move the help links selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLinks() {
      const menuAnnouncementLinks = document.querySelector('.mob__announcement-links');
      if (!theme.mediaMatches.md && !menuAnnouncementLinks) {
        const mobNav = document.createElement('nav');
        mobNav.classList.add('mob__announcement-links');

        const mobNavUl = document.createElement('ul');
        mobNav.classList.add('secondary-nav');

        this.links.forEach((link) => {
          mobNav.innerHTML += `<li><a class="secondary-nav__item" href="${link.href}">${link.innerText}</a>`;
        });

        mobNav.appendChild(mobNavUl);
        this.menu.appendChild(mobNav);
      } else if (theme.mediaMatches.md && menuAnnouncementLinks) {
        menuAnnouncementLinks.remove();
      }
    }

    pauseSlider() {
      this.isPaused = true;
    }

    resumeSlider() {
      this.isPaused = false;
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}
